import React, { useState, useMemo } from 'react';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import { DownloadOutlined } from '@ant-design/icons';
import {
  Form,
  Divider,
  Input,
  Button,
  Space,
  message,
  Select,
  DatePicker,
  Typography,
  Popover,
  QRCode,
} from 'antd';

import styles from './detail.module.scss';
import FormRowInfo from 'components/ui/FormRowInfo';
import API from '../../helpers/API';
import { BottomButtons, HeadingH1 } from 'components/ui';
import { getDisplayName } from 'utils/getDisplayName';
import { formatSeoulDateTime } from 'utils/Formatter';
import { getTankChargeStatus, getTankUseStatus } from 'utils/CodeMapper';
import {
  tankManufacturerOptions,
  valveManufacturerOptions,
  valveVersionOptions,
} from './formOptions';
import { canChangeTankStatus } from './tankStatusPermissions';
import ValveHistoryModal from './valves';

const cx = classNames.bind(styles);

const Page = () => {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [tank, setTank] = useState({
    qrcode: '',
    useStatus: '',
    chargeStatus: '',
    locationRid: '',
    type: '',
    size: '',
    region: '',
    serialNo: '',
    manufacturer: '',
    manufacturedDate: '',
    expireDate: '',
    valveManufacturedAt: '',
    velveSerialNo: '',
    velveManufacturer: '',
    velveVersion: '',
    velveExpireDate: '',
    chargeCount: 0,
    memo: '',
  });
  const [locations, setLocations] = useState();
  const [valveHistoryOpen, setValveHistoryOpen] = useState(false);
  const statusEditable = canChangeTankStatus(
    localStorage.getItem('@h2tms/role'),
  );

  const loadTankDetail = () => {
    API.get('/locations').then(({ success, data }) => {
      if (success) {
        setLocations(data.locations);
      }
    });

    API.get(`/tanks/${rid}`).then(({ success, data }) => {
      if (success) {
        setTank({
          ...data,
          locationRid: data.location?.rid ?? '',
          type: data.type ?? '',
          size: data.size ?? '',
          region: data.region ?? '',
          serialNo: data.serialNo ?? '',
          manufacturer: data.manufacturer ?? '',
          manufacturedDate: data.manufacturedDate
            ? dayjs(data.manufacturedDate, 'YYYYMMDD')
            : '',
          expireDate: data.expireDate
            ? dayjs(data.expireDate, 'YYYYMMDD')
            : '',
          valveManufacturedAt: data.valveManufacturedAt
            ? dayjs(data.valveManufacturedAt, 'YYYYMMDD')
            : '',
          velveSerialNo: data.velveSerialNo ?? '',
          velveManufacturer: data.velveManufacturer ?? '',
          velveVersion: data.velveVersion ?? '',
          velveExpireDate: data.velveExpireDate
            ? dayjs(data.velveExpireDate, 'YYYYMMDD')
            : '',
          chargeCount: data.chargeCount ?? 0,
          memo: data.memo ?? '',
        });
      }
    });
  };

  useMountEffect(() => {
    loadTankDetail();
  });

  useUpdateEffect(() => {
    if (!tank.manufacturedDate) return;

    setTank({
      ...tank,
      expireDate: tank.manufacturedDate.clone().add(expireYears, 'years'),
    });
  }, [tank.manufacturedDate]);

  useUpdateEffect(() => {
    if (!tank.valveManufacturedAt) return;

    setTank({
      ...tank,
      velveExpireDate: tank.valveManufacturedAt.clone().add(5, 'years'),
    });
  }, [tank.valveManufacturedAt]);

  const handleChange = (key, value) => {
    setTank({
      ...tank,
      [key]: value,
    });
  };

  const handleChangeUseStatus = (value) => {
    if (value === 'INUSE') {
      setTank({
        ...tank,
        useStatus: 'INUSE',
        chargeStatus: 'CHARGED',
        locationRid: locations?.find((location) => location.name === '주문고객')
          .rid,
        manufacturedDate: dayjs(),
      });
    } else {
      setTank({
        ...tank,
        useStatus: value,
        chargeStatus: 'UNCHARGED',
        locationRid: '',
        manufacturedDate: dayjs(),
      });
    }
  };

  const doRemove = () => {
    if (!window.confirm('용기를 삭제하시겠습니까?')) {
      return;
    }

    API.delete('/tanks', { data: { rids: [rid] } }).then(({ success }) => {
      if (success) {
        navigate('/tanks');
      } else {
        message.error('삭제를 실패했습니다.');
      }
    });
  };

  const doRetire = () => {
    if (!window.confirm('용기를 폐기하시겠습니까?')) {
      return;
    }

    API.patch('/tanks/retire', { rids: [rid] }).then(({ success }) => {
      if (success) {
        navigate('/tanks');
      } else {
        message.error('폐기를 실패했습니다.');
      }
    });
  };

  const validation = () => {
    if (isDraft) {
      return true;
    }
    if (tank.locationRid === '') {
      message.error('현재 위치가 선택되지 않았습니다.');
      return false;
    }
    if (tank.type === '') {
      message.error('유형이 선택되지 않았습니다.');
      return false;
    }
    if (tank.size === '') {
      message.error('용량이 선택되지 않았습니다.');
      return false;
    }
    if (tank.region === '') {
      message.error('지역이 선택되지 않았습니다.');
      return false;
    }
    if (tank.serialNo.trim() === '') {
      message.error('용기 일련번호가 입력되지 않았습니다.');
      return false;
    }
    if (tank.manufacturer === '') {
      message.error('용기 제조사가 선택되지 않았습니다.');
      return false;
    }
    if (tank.velveSerialNo.trim() === '') {
      message.error('밸브 일련번호가 입력되지 않았습니다.');
      return false;
    }
    if (tank.velveManufacturer === '') {
      message.error('밸브 제조사가 선택되지 않았습니다.');
      return false;
    }
    if (tank.velveVersion === '') {
      message.error('밸브 버전이 선택되지 않았습니다.');
      return false;
    }
    if (!tank.valveManufacturedAt) {
      message.error('밸브 제조 일자가 선택되지 않았습니다.');
      return false;
    }

    return true;
  };

  const doSave = () => {
    if (!validation()) {
      return;
    }

    const data = {
      qrcode: tank.qrcode,
      useStatus: tank.useStatus,
      chargeStatus: tank.chargeStatus,
      locationRid: tank.locationRid,
      type: tank.type,
      size: tank.size,
      region: tank.region,
      serialNo: tank.serialNo,
      manufacturer: tank.manufacturer,
      manufacturedDate: tank.manufacturedDate
        ? tank.manufacturedDate.format('YYYYMMDD')
        : '',
      expireDate: tank.expireDate ? tank.expireDate.format('YYYYMMDD') : '',
      valveManufacturedAt: tank.valveManufacturedAt
        ? tank.valveManufacturedAt.format('YYYYMMDD')
        : '',
      velveSerialNo: tank.velveSerialNo,
      velveManufacturer: tank.velveManufacturer,
      velveVersion: tank.velveVersion,
      velveExpireDate: tank.velveExpireDate
        ? tank.velveExpireDate.format('YYYYMMDD')
        : '',
      memo: tank.memo,
    };

    API.patch(`/tanks/${rid}`, data).then(({ success }) => {
      if (success) {
        message.success('저장을 완료했습니다.');
        navigate('/tanks');
      } else {
        message.error('저장을 실패했습니다.');
      }
    });
  };

  const downloadQRCode = (id) => {
    const canvas = document.getElementById(id)?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL();
      const a = document.createElement('a');
      a.download = `${id}_QRCode.png`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const isDraft = useMemo(() => tank.useStatus === 'DRAFT', [tank]);
  const isInUse = useMemo(() => tank.useStatus === 'INUSE', [tank]);
  const isReady = useMemo(() => tank.useStatus === 'READY', [tank]);
  const expireYears = useMemo(
    () => (tank.region === 'EU/USA' ? 20 : 15),
    [tank.region],
  );

  return (
    <div className={cx('container')}>
      <Space direction="vertical" size={16} className={cx('wrapper')}>
        <HeadingH1 title={'용기 상세'} />
        <Form layout="vertical" className={cx('form')}>
          <Typography.Text className={cx('title')} strong>
            용기 정보
          </Typography.Text>
          <div className={cx('sectionPanel', 'sectionPanelWide')}>
            <div className={cx('infoClusterRow')}>
              <div className={cx('infoClusterPanel', 'infoClusterPrimary')}>
                <div className={cx('infoClusterGridPrimary')}>
                  <Form.Item className={cx('formItemCompact')} label="QR 번호">
                    <Input value={tank.qrcode} readOnly className={cx('fullWidthControl')} />
                  </Form.Item>
                  <Form.Item className={cx('formItemCompact')} label="용기 일련번호" required>
                    <Input
                      placeholder="일련번호"
                      value={tank.serialNo || ''}
                      disabled={isDraft}
                      onChange={(e) => handleChange('serialNo', e.target.value)}
                      className={cx('fullWidthControl')}
                    />
                  </Form.Item>
                  <Form.Item className={cx('formItemCompact')} label="QR 코드">
                    <div className={cx('qrActionField')}>
                      <Popover
                        id={`${tank.qrcode}_QR`}
                        overlayInnerStyle={{ padding: 0 }}
                        content={<QRCode size={200} value={tank.qrcode} bordered={false} />}>
                        <Button
                          className={cx('qrActionButton')}
                          onClick={() => downloadQRCode(`${tank.qrcode}_QR`)}
                          icon={<DownloadOutlined />}
                        />
                      </Popover>
                    </div>
                  </Form.Item>
                </div>
              </div>
              <div className={cx('infoClusterPanel', 'infoClusterStatus')}>
                <div className={cx('infoClusterGridStatus')}>
                  <Form.Item
                    className={cx('formItemCompact')}
                    label="가용상태"
                    required={statusEditable}>
                    <Space className={cx('fieldWithInfo')}>
                      <Select
                        placeholder="선택"
                        value={tank.useStatus}
                        onChange={handleChangeUseStatus}
                        disabled={!statusEditable}
                        options={[
                          { value: 'DRAFT', label: '임시', disabled: true },
                          { value: 'READY', label: '준비' },
                          { value: 'INUSE', label: '사용' },
                          { value: 'INSPECTING', label: '검사' },
                          { value: 'RETIRED', label: '폐기', disabled: true },
                          { value: 'RECALL', label: '리콜' },
                          { value: 'DISABLED', label: '사용중지' },
                        ]}
                        className={cx('fullWidthControl')}
                      />
                      {!statusEditable && (
                        <FormRowInfo>ADMIN만 변경 가능</FormRowInfo>
                      )}
                    </Space>
                  </Form.Item>
                  <Form.Item className={cx('formItemCompact')} label="충전상태" required={isReady}>
                    <Space className={cx('fieldWithInfo')}>
                      <Select
                        placeholder="선택"
                        value={tank.chargeStatus}
                        onChange={(value) => handleChange('chargeStatus', value)}
                        disabled={!statusEditable}
                        options={[
                          { value: 'CHARGED', label: '충전' },
                          { value: 'UNCHARGED', label: '미충전' },
                        ]}
                        className={cx('fullWidthControl')}
                      />
                      {!statusEditable && (
                        <FormRowInfo>ADMIN만 변경 가능</FormRowInfo>
                      )}
                    </Space>
                  </Form.Item>
                </div>
              </div>
            </div>
            <div className={cx('infoGridThree')}>
              <Form.Item
                className={cx('formItemCompact')}
                label="현재 위치"
                required={!isDraft && !isInUse}>
                <Space className={cx('fieldWithInfo')}>
                  <Select
                    placeholder="선택"
                    value={tank.locationRid || null}
                    disabled={isDraft || isInUse}
                    onChange={(value) => handleChange('locationRid', value)}
                    options={locations?.map((location) => {
                      const option = { value: location.rid, label: location.name };
                      if (location.name === '주문고객') {
                        option.disabled = true;
                      }
                      return option;
                    })}
                    className={cx('fullWidthControl')}
                  />
                  {isInUse && (
                    <FormRowInfo>사용 상태 시, 임의 변경 불가</FormRowInfo>
                  )}
                </Space>
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="유형" required={!isDraft}>
                <Select
                  placeholder="선택"
                  value={tank.type || null}
                  disabled={isDraft}
                  onChange={(value) => handleChange('type', value)}
                  options={[
                    { value: 'TYPE1', label: 'TYPE1' },
                    { value: 'TYPE2', label: 'TYPE2' },
                    { value: 'TYPE3', label: 'TYPE3' },
                    { value: 'TYPE4', label: 'TYPE4' },
                  ]}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="용량" required={!isDraft}>
                <Select
                  placeholder="선택"
                  value={tank.size || null}
                  disabled={isDraft}
                  onChange={(value) => handleChange('size', value)}
                  options={[
                    { value: 7, label: '7L' },
                    { value: 9, label: '9L' },
                    { value: 10.8, label: '10.8L' },
                    { value: 12, label: '12L' },
                  ]}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
            </div>
            <div className={cx('infoGridThree')}>
              <Form.Item className={cx('formItemCompact')} label="지역" required={!isDraft}>
                <Select
                  placeholder="선택"
                  value={tank.region || null}
                  disabled={isDraft}
                  onChange={(value) => handleChange('region', value)}
                  options={[
                    { value: 'KOREA', label: 'KOREA' },
                    { value: 'EU/USA', label: 'EU/USA' },
                  ]}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="충전소">
                <div className={cx('readonlyField')}>MS가스</div>
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="충전횟수">
                <div className={cx('readonlyField')}>
                  {tank.chargeCount ?? 0}
                </div>
              </Form.Item>
            </div>
            <div className={cx('infoGridThree', 'infoGridTankLifecycle')}>
              <Form.Item className={cx('formItemCompact')} label="용기 제조사" required={!isDraft}>
                <Select
                  placeholder="선택"
                  disabled={isDraft}
                  value={tank.manufacturer || null}
                  onChange={(value) => handleChange('manufacturer', value)}
                  options={tankManufacturerOptions}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="용기 제조 일자" required={!isDraft}>
                <DatePicker
                  placeholder="용기 제조 일자"
                  value={tank.manufacturedDate || null}
                  onChange={(value) => handleChange('manufacturedDate', value)}
                  disabled={isDraft}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="용기 충전기한" required={!isDraft}>
                <Space className={cx('fieldWithInfo')}>
                  <DatePicker
                    placeholder="용기 충전기한"
                    disabled={isDraft}
                    value={tank.expireDate || null}
                    onChange={(value) => handleChange('expireDate', value)}
                    className={cx('fullWidthControl')}
                  />
                  <div className={cx('noWrapInfo')}>
                    <FormRowInfo>기본값 : 제조일자로부터 15년</FormRowInfo>
                  </div>
                </Space>
              </Form.Item>
            </div>
          </div>
          <Divider />

          <Typography.Text className={cx('title')} strong>
            밸브 정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridThree')}>
              <Form.Item className={cx('formItemCompact')} label="밸브 일련번호" required={!isDraft}>
                <Input
                  placeholder="밸브 일련번호"
                  value={tank.velveSerialNo}
                  onChange={(e) => handleChange('velveSerialNo', e.target.value)}
                  disabled={isDraft}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="밸브 제조사" required={!isDraft}>
                <Select
                  placeholder="선택"
                  disabled={isDraft}
                  value={tank.velveManufacturer || null}
                  onChange={(value) => handleChange('velveManufacturer', value)}
                  options={valveManufacturerOptions}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="밸브 제조 일자" required={!isDraft}>
                <DatePicker
                  placeholder="밸브 제조 일자"
                  disabled={isDraft}
                  value={tank.valveManufacturedAt || null}
                  onChange={(value) => handleChange('valveManufacturedAt', value)}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
            </div>
            <div className={cx('infoGridTwo')}>
              <Form.Item
                className={cx('formItemCompact')}
                label={
                  <Space size={8}>
                    <span>밸브 버전</span>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setValveHistoryOpen(true)}>
                      밸브 이력
                    </Button>
                  </Space>
                }
                required={!isDraft}>
                <Select
                  placeholder="선택"
                  value={tank.velveVersion || null}
                  disabled={isDraft}
                  onChange={(value) => handleChange('velveVersion', value)}
                  options={valveVersionOptions}
                  className={cx('fullWidthControl')}
                />
              </Form.Item>
              <Form.Item className={cx('formItemCompact')} label="밸브 사용 기한" required={!isDraft}>
                <Space>
                  <DatePicker
                    placeholder="밸브 사용 기한"
                    disabled={isDraft}
                    value={tank.velveExpireDate || null}
                    onChange={(value) => handleChange('velveExpireDate', value)}
                    className={cx('fullWidthControl')}
                  />
                  <FormRowInfo>기본값 : 밸브 제조 일자로부터 5년</FormRowInfo>
                </Space>
              </Form.Item>
            </div>
          </div>
          <Divider />

          <Typography.Text className={cx('title')} strong>
            부가정보
          </Typography.Text>
          <div className={cx('additionalInfoGrid')}>
            <div className={cx('detailCard')}>
              <div className={cx('detailCardHeader')}>
                <Typography.Text strong>메모</Typography.Text>
              </div>
              <Input.TextArea
                className={cx('memoTextarea')}
                value={tank.memo ?? ''}
                onChange={(e) => handleChange('memo', e.target.value)}
              />
            </div>
            <div className={cx('detailCard')}>
              <div className={cx('detailCardHeader')}>
                <Typography.Text strong>이력 요약</Typography.Text>
                <Typography.Text type="secondary">서울 시간 기준</Typography.Text>
              </div>
              <div className={cx('metaGrid')}>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>등록자</span>
                  <span className={cx('metaValue')}>
                    {getDisplayName(tank.createdBy)}
                  </span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>등록일시</span>
                  <span className={cx('metaValue', 'metaValueCompact')}>
                    {formatSeoulDateTime(tank.createdAt)}
                  </span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>수정자</span>
                  <span className={cx('metaValue')}>
                    {getDisplayName(tank.updatedBy)}
                  </span>
                </div>
                <div className={cx('metaCard')}>
                  <span className={cx('metaLabel')}>수정일시</span>
                  <span className={cx('metaValue', 'metaValueCompact')}>
                    {formatSeoulDateTime(tank.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <BottomButtons
            left={[
              <Button key="backPage" onClick={() => navigate(-1)}>
                뒤로
              </Button>,
              <Button key="removeTank" danger type="primary" onClick={doRemove}>
                삭제
              </Button>,
              <Button key="retireTank" danger type="primary" onClick={doRetire}>
                폐기
              </Button>,
            ]}
            right={[
              <Button key="moveValveHistory" type="primary" onClick={() => setValveHistoryOpen(true)}>
                밸브 이력
              </Button>,
              <Button key="saveTank" type="primary" onClick={doSave}>
                저장
              </Button>,
            ]}
          />
        </Form>
      </Space>
      <ValveHistoryModal
        rid={rid}
        tank={tank}
        open={valveHistoryOpen}
        onClose={() => setValveHistoryOpen(false)}
        onChanged={loadTankDetail}
      />
    </div>
  );
};

export default Page;
